import React, { useState, useEffect } from 'react';
import {
  User, Briefcase, Shield, ChevronDown, Save, Check,
  LogOut, AlertTriangle, Mail, Sun, Moon,
  Link2, Pencil, Plus, Trash2, Zap, RefreshCw,
} from 'lucide-react';
import { useUserData } from '../context/UserDataContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import AccountLinkModal, { ConnectionBadge, AccountTypeChip } from './AccountLinkModal';
import { unlinkPlaidItem, syncAllPlaidData, buildLinkedAccounts } from '../lib/plaidSync';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming',
];

function Section({ title, icon, children }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-700/60 bg-slate-800/80">
        <div className="text-slate-400">{icon}</div>
        <h3 className="font-semibold text-slate-200 text-sm">{title}</h3>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start">
      <div>
        <div className="text-sm font-medium text-slate-300">{label}</div>
        {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <div className="sm:col-span-2">{children}</div>
    </div>
  );
}

const inputCls = `w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200
  placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors`;

const selectCls = `w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200
  focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none`;

function computeAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

function AccountsSection() {
  const { userProfile, updateSection, linkedAccountsData, budgetData, netWorthData, investingData } = useUserData();
  const [showModal,   setShowModal]   = useState(false);
  const [editId,      setEditId]      = useState(null);
  const [editBal,     setEditBal]     = useState('');
  const [confirmDel,  setConfirmDel]  = useState(null);
  const [unlinking,   setUnlinking]   = useState(null);  // account id being unlinked
  const [syncing,     setSyncing]     = useState(false);
  const [syncMsg,     setSyncMsg]     = useState('');

  const accounts = linkedAccountsData;

  const deleteAccount = async (id) => {
    const acc = accounts.find(a => a.id === id);
    // If it's a Plaid account, call the unlink API first
    if (acc?.connectionType === 'plaid' && acc?.plaidItemId) {
      setUnlinking(id);
      try {
        await unlinkPlaidItem(acc.plaidItemId);
      } catch (err) {
        console.warn('Plaid unlink API error (removing locally anyway):', err.message);
      }
      setUnlinking(null);
    }
    const updated = accounts.filter(a => a.id !== id);
    updateSection('user_profile', { ...userProfile, linkedAccounts: updated });
    setConfirmDel(null);
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const synced = await syncAllPlaidData({ budgetData, netWorthData, investingData });
      const updatedLinked = buildLinkedAccounts(synced.accounts, userProfile?.linkedAccounts || []);
      await updateSection('user_profile', { ...userProfile, linkedAccounts: updatedLinked });
      if (synced.newBudget !== budgetData)    await updateSection('budget',   synced.newBudget);
      if (synced.newNetWorth !== netWorthData) await updateSection('net_worth', synced.newNetWorth);
      if (synced.newInvesting && synced.newInvesting !== investingData)
        await updateSection('investing', synced.newInvesting);
      setSyncMsg(synced.errors.length ? `Synced (${synced.errors.length} warning)` : 'Synced!');
    } catch (err) {
      setSyncMsg('Sync failed: ' + err.message);
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(''), 3000);
    }
  };

  const saveBalance = (id) => {
    const bal = parseFloat(editBal);
    if (isNaN(bal)) { setEditId(null); return; }
    const updated = accounts.map(a => a.id === id
      ? { ...a, balance: bal, lastUpdated: new Date().toISOString() }
      : a
    );
    updateSection('user_profile', { ...userProfile, linkedAccounts: updated });
    setEditId(null);
  };

  const totalAssets = accounts
    .filter(a => !['credit_card', 'mortgage'].includes(a.type))
    .reduce((s, a) => s + (a.balance || 0), 0);
  const totalLiabilities = accounts
    .filter(a => ['credit_card', 'mortgage'].includes(a.type))
    .reduce((s, a) => s + (a.balance || 0), 0);

  return (
    <Section title="Accounts & Connections" icon={<Link2 size={17} />}>
      {/* Summary row */}
      {accounts.length > 0 && (
        <div className="grid grid-cols-3 gap-3 pb-1">
          {[
            { label: 'Total Accounts',  val: accounts.length,      fmt: v => v,            color: 'text-slate-200'   },
            { label: 'Total Assets',    val: totalAssets,           fmt: fmt,               color: 'text-emerald-400' },
            { label: 'Total Liabilities', val: totalLiabilities,   fmt: fmt,               color: 'text-rose-400'    },
          ].map(c => (
            <div key={c.label} className="bg-slate-900/60 rounded-xl p-3 text-center border border-slate-700/40">
              <div className="text-lg font-bold tabular-nums {c.color}"
                style={{ color: c.color.includes('emerald') ? '#34d399' : c.color.includes('rose') ? '#f87171' : '#e2e8f0' }}>
                {c.fmt(c.val)}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Account list */}
      {accounts.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Link2 size={28} className="mx-auto mb-3 text-slate-600" />
          <p className="text-sm font-medium text-slate-400">No accounts linked yet</p>
          <p className="text-xs mt-1">Connect your bank or brokerage to see live data across Finovo.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.map(acc => (
            <div key={acc.id} className="flex items-center gap-3 p-3 bg-slate-900/50 border border-slate-700/40 rounded-xl hover:border-slate-600/60 transition-colors">
              {/* Icon */}
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-sm ${
                acc.connectionType === 'plaid' ? 'bg-emerald-900/40 border border-emerald-700/30' : 'bg-slate-700/60 border border-slate-600/40'
              }`}>
                {acc.connectionType === 'plaid' ? <Zap size={15} className="text-emerald-400" /> : <Pencil size={15} className="text-slate-400" />}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-200 truncate">{acc.name}</span>
                  <ConnectionBadge account={acc} size="xs" />
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-xs text-slate-500">{acc.institution || '—'}</span>
                  <AccountTypeChip type={acc.type} />
                  {acc.lastUpdated && (
                    <span className="text-xs text-slate-600">
                      Updated {new Date(acc.lastUpdated).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Balance (editable for manual) */}
              <div className="flex-shrink-0 text-right">
                {editId === acc.id ? (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-500">$</span>
                    <input
                      autoFocus
                      type="number"
                      value={editBal}
                      onChange={e => setEditBal(e.target.value)}
                      onBlur={() => saveBalance(acc.id)}
                      onKeyDown={e => { if (e.key === 'Enter') saveBalance(acc.id); if (e.key === 'Escape') setEditId(null); }}
                      className="w-24 bg-slate-800 border border-blue-500 rounded px-2 py-1 text-xs text-slate-100 focus:outline-none"
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => acc.connectionType === 'manual' ? (setEditId(acc.id), setEditBal(String(acc.balance ?? ''))) : undefined}
                    className={`text-sm font-bold tabular-nums ${acc.connectionType === 'manual' ? 'text-slate-200 hover:text-blue-300 cursor-pointer' : 'text-slate-200 cursor-default'}`}
                    title={acc.connectionType === 'manual' ? 'Click to edit balance' : 'Synced via Plaid'}
                  >
                    {fmt(acc.balance || 0)}
                  </button>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {acc.connectionType === 'plaid' && (
                  <button
                    onClick={() => {
                      const updated = accounts.map(a => a.id === acc.id
                        ? { ...a, lastUpdated: new Date().toISOString() }
                        : a);
                      updateSection('user_profile', { ...userProfile, linkedAccounts: updated });
                    }}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-blue-400 hover:bg-slate-700 transition-colors"
                    title="Refresh"
                  ><RefreshCw size={12} /></button>
                )}
                {unlinking === acc.id ? (
                  <div className="flex items-center gap-1.5 text-xs text-slate-400">
                    <RefreshCw size={10} className="animate-spin" /> Unlinking…
                  </div>
                ) : confirmDel === acc.id ? (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-400">Remove?</span>
                    <button onClick={() => deleteAccount(acc.id)}
                      className="px-2 py-0.5 bg-rose-600 hover:bg-rose-500 text-white text-[10px] font-semibold rounded transition-colors">
                      Yes
                    </button>
                    <button onClick={() => setConfirmDel(null)}
                      className="px-2 py-0.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-[10px] rounded transition-colors">
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDel(acc.id)}
                    className="p-1.5 rounded-lg text-slate-600 hover:text-rose-400 hover:bg-slate-700 transition-colors"
                    title="Remove account"
                  ><Trash2 size={12} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-3 pt-1 flex-wrap">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-xl transition-colors shadow-lg shadow-blue-900/30"
        >
          <Plus size={14} /> Add Account
        </button>
        {accounts.some(a => a.connectionType === 'plaid') && (
          <button
            onClick={handleSyncAll}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-sm font-semibold rounded-xl transition-colors border border-slate-600"
          >
            <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing…' : 'Sync All'}
          </button>
        )}
        {syncMsg && (
          <span className={`text-xs font-medium ${syncMsg.includes('failed') ? 'text-rose-400' : 'text-emerald-400'}`}>
            {syncMsg}
          </span>
        )}
      </div>

      {showModal && (
        <AccountLinkModal
          onClose={() => setShowModal(false)}
          onAccountAdded={() => {}}
        />
      )}
    </Section>
  );
}

export default function Settings({ onSignOut, supabaseUser }) {
  const { userProfile, updateSection } = useUserData();
  const { theme, toggleTheme } = useTheme();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Local form state (mirrors userProfile)
  const [form, setForm] = useState({
    name:              userProfile?.name              || '',
    dob:               userProfile?.dob               || '',
    taxFilingStatus:   userProfile?.taxFilingStatus   || 'single',
    annualIncome:      userProfile?.annualIncome      || 0,
    stateOfResidence:  userProfile?.stateOfResidence  || '',
    employmentStatus:  userProfile?.employmentStatus  || 'employed',
    riskTolerance:     userProfile?.riskTolerance     || 'moderate',
    retirementAge:     userProfile?.retirementAge     || 65,
    lifeExpectancy:    userProfile?.lifeExpectancy    || 90,
    ssEstimate:        userProfile?.ssEstimate        || 0,
  });

  // Sync form when profile loads
  useEffect(() => {
    if (userProfile) {
      setForm(f => ({
        ...f,
        name:             userProfile.name             || f.name,
        dob:              userProfile.dob              || f.dob,
        taxFilingStatus:  userProfile.taxFilingStatus  || f.taxFilingStatus,
        annualIncome:     userProfile.annualIncome     || f.annualIncome,
        stateOfResidence: userProfile.stateOfResidence || f.stateOfResidence,
        employmentStatus: userProfile.employmentStatus || f.employmentStatus,
        riskTolerance:    userProfile.riskTolerance    || f.riskTolerance,
        retirementAge:    userProfile.retirementAge    || f.retirementAge,
        lifeExpectancy:   userProfile.lifeExpectancy   || f.lifeExpectancy,
        ssEstimate:       userProfile.ssEstimate       || f.ssEstimate,
      }));
    }
  }, [userProfile]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    const computedAge = computeAge(form.dob);
    await updateSection('user_profile', {
      ...userProfile,
      ...form,
      age: computedAge ?? userProfile?.age ?? 30,
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const computedAge = computeAge(form.dob);
  const email = supabaseUser?.email || userProfile?.email || '';

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Settings</h1>
        <p className="text-slate-400 text-sm mt-0.5">Manage your profile and preferences</p>
      </div>

      {/* Personal Info */}
      <Section title="Personal Information" icon={<User size={17} />}>
        <Field label="Display Name" hint="Shown in your dashboard greeting">
          <input
            className={inputCls}
            value={form.name}
            onChange={e => set('name', e.target.value)}
            placeholder="Your name"
          />
        </Field>
        <Field label="Email Address" hint="Linked to your account">
          <div className="flex items-center gap-2">
            <input
              className={`${inputCls} flex-1 opacity-60 cursor-not-allowed`}
              value={email}
              readOnly
              tabIndex={-1}
            />
            <Mail size={15} className="text-slate-500 flex-shrink-0" />
          </div>
          <p className="text-xs text-slate-600 mt-1">To change your email, use the Account section below.</p>
        </Field>
        <Field label="Date of Birth" hint="Used to calculate your current age">
          <div className="flex items-center gap-3">
            <input
              type="date"
              className={`${inputCls} flex-1`}
              value={form.dob}
              onChange={e => set('dob', e.target.value)}
            />
            {computedAge !== null && (
              <span className="text-sm font-medium text-slate-400 flex-shrink-0">Age: {computedAge}</span>
            )}
          </div>
        </Field>
      </Section>

      {/* Financial Profile */}
      <Section title="Financial Profile" icon={<Briefcase size={17} />}>
        <Field label="Tax Filing Status">
          <div className="relative">
            <select
              className={selectCls}
              value={form.taxFilingStatus}
              onChange={e => set('taxFilingStatus', e.target.value)}
            >
              <option value="single">Single</option>
              <option value="married_joint">Married Filing Jointly</option>
              <option value="married_separate">Married Filing Separately</option>
              <option value="head_of_household">Head of Household</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          </div>
        </Field>
        <Field label="Annual Income" hint="Gross income before taxes">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
            <input
              type="number"
              className={`${inputCls} pl-7`}
              value={form.annualIncome || ''}
              onChange={e => set('annualIncome', Number(e.target.value))}
              placeholder="0"
              min={0}
            />
          </div>
        </Field>
        <Field label="State of Residence" hint="For state tax considerations">
          <div className="relative">
            <select
              className={selectCls}
              value={form.stateOfResidence}
              onChange={e => set('stateOfResidence', e.target.value)}
            >
              <option value="">Select state…</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          </div>
        </Field>
        <Field label="Employment Status">
          <div className="relative">
            <select
              className={selectCls}
              value={form.employmentStatus}
              onChange={e => set('employmentStatus', e.target.value)}
            >
              <option value="employed">Employed (W-2)</option>
              <option value="self_employed">Self-Employed / 1099</option>
              <option value="business_owner">Business Owner</option>
              <option value="retired">Retired</option>
              <option value="unemployed">Unemployed</option>
              <option value="student">Student</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          </div>
        </Field>
        <Field label="Risk Tolerance" hint="Guides investing & retirement advice">
          <div className="relative">
            <select
              className={selectCls}
              value={form.riskTolerance}
              onChange={e => set('riskTolerance', e.target.value)}
            >
              <option value="conservative">Conservative</option>
              <option value="moderate">Moderate</option>
              <option value="aggressive">Aggressive</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          </div>
        </Field>
      </Section>

      {/* Retirement Planning Defaults */}
      <Section title="Retirement Planning" icon={<Briefcase size={17} />}>
        <Field label="Target Retirement Age">
          <input
            type="number"
            className={inputCls}
            value={form.retirementAge}
            onChange={e => set('retirementAge', Number(e.target.value))}
            min={40}
            max={80}
          />
        </Field>
        <Field label="Life Expectancy" hint="Used in Monte Carlo projections">
          <input
            type="number"
            className={inputCls}
            value={form.lifeExpectancy}
            onChange={e => set('lifeExpectancy', Number(e.target.value))}
            min={65}
            max={110}
          />
        </Field>
        <Field label="Social Security Estimate" hint="Monthly benefit at full retirement age">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
            <input
              type="number"
              className={`${inputCls} pl-7`}
              value={form.ssEstimate || ''}
              onChange={e => set('ssEstimate', Number(e.target.value))}
              placeholder="0"
              min={0}
            />
          </div>
        </Field>
      </Section>

      {/* Appearance */}
      <Section title="Appearance" icon={<Sun size={17} />}>
        <Field label="Color Theme" hint="Toggle between dark and light mode">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                toggleTheme();
                // Persist theme preference to Supabase
                updateSection('user_profile', {
                  ...userProfile,
                  theme: theme === 'dark' ? 'light' : 'dark',
                });
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                theme === 'light' ? 'bg-blue-600' : 'bg-slate-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  theme === 'light' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <div className="flex items-center gap-2">
              {theme === 'light' ? <Sun size={15} className="text-amber-400" /> : <Moon size={15} className="text-slate-400" />}
              <span className="text-sm text-slate-300">{theme === 'light' ? 'Light Mode' : 'Dark Mode'}</span>
            </div>
          </div>
        </Field>
      </Section>

      {/* Accounts & Connections */}
      <AccountsSection />

      {/* Account */}
      <Section title="Account" icon={<Shield size={17} />}>
        <Field label="Change Password" hint="We'll send a reset link to your email">
          <button
            onClick={async () => {
              if (email) {
                await supabase.auth.resetPasswordForEmail(email);
                alert('Password reset email sent! Check your inbox.');
              }
            }}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-200 text-sm font-medium rounded-lg transition-colors"
          >
            Send Reset Email
          </button>
        </Field>
        <Field label="Sign Out" hint="Sign out of your Finovo account">
          <button
            onClick={onSignOut}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-rose-900/40 border border-slate-600 hover:border-rose-700/60 text-slate-300 hover:text-rose-300 text-sm font-medium rounded-lg transition-colors"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </Field>
        <div className="pt-3 border-t border-slate-700/60">
          <div className="flex items-start gap-2.5 p-3 bg-rose-950/30 border border-rose-800/40 rounded-lg">
            <AlertTriangle size={15} className="text-rose-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-xs font-semibold text-rose-300 mb-0.5">Danger Zone</div>
              <div className="text-xs text-slate-400">
                To delete your account and all data permanently, please contact{' '}
                <a href="mailto:support@finovo.app" className="text-blue-400 hover:text-blue-300">
                  support@finovo.app
                </a>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Save button */}
      <div className="flex justify-end pb-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-lg ${
            saved
              ? 'bg-emerald-600 text-white shadow-emerald-900/40'
              : 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/40'
          }`}
        >
          {saved ? <><Check size={15} /> Saved!</> : saving ? 'Saving…' : <><Save size={15} /> Save Changes</>}
        </button>
      </div>
    </div>
  );
}
